import type { KeyboardEvent, RefObject } from 'react'
import { Loader2, Paperclip, Send } from 'lucide-react'
import { WebSearchModeToggle } from '@/components/WebSearchModeToggle'
import { CHAT_MESSAGE_MAX_CHARS } from '@/lib/chat-limits'
import type { WebSearchMode } from '@/lib/settings'

type ChatInputBarProps = {
  chatUploadInfo: string | null
  chatUploadJobId: string | null
  uploadInputRef: RefObject<HTMLInputElement | null>
  acceptAttr: string
  docsUploading: boolean
  onUploadFiles: (files: FileList | null) => void
  onToggleDocsPicker: () => void
  showDocsPicker: boolean
  selectedDocCount: number
  webSearchMode: WebSearchMode
  onWebSearchModeChange: (mode: WebSearchMode) => void
  isPremium: boolean
  canDocs?: boolean
  canWeb?: boolean
  onUpgrade: () => void
  isLoading: boolean
  input: string
  onInputChange: (value: string) => void
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  chatInputRef: RefObject<HTMLTextAreaElement | null>
  inputMinHeight: number
  inputMaxHeight: number
  onSend: () => void
}

export function ChatInputBar({
  chatUploadInfo,
  chatUploadJobId,
  uploadInputRef,
  acceptAttr,
  docsUploading,
  onUploadFiles,
  onToggleDocsPicker,
  showDocsPicker,
  selectedDocCount,
  webSearchMode,
  onWebSearchModeChange,
  isPremium,
  canDocs,
  canWeb,
  onUpgrade,
  isLoading,
  input,
  onInputChange,
  onKeyDown,
  chatInputRef,
  inputMinHeight,
  inputMaxHeight,
  onSend,
}: ChatInputBarProps) {
  // Free: ограниченный доступ N/день. Если canDocs/canWeb не переданы — старое поведение (premium-only).
  const _canDocs = canDocs ?? isPremium
  const _canWeb = canWeb ?? isPremium
  return (
    <div
      className="chat-input-bar sticky bottom-0 glass-header"
      style={{
        padding: '10px 16px',
        paddingBottom: 'calc(10px + var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
        borderBottom: 'none',
        borderTop: '1px solid var(--glass-border-subtle)',
      }}
    >
      {(chatUploadInfo || chatUploadJobId) && (
        <p className="text-[10px] text-telegram-button" style={{ marginBottom: '6px' }}>
          {chatUploadInfo || 'Документ загружен и обрабатывается…'}
        </p>
      )}
      <input
        ref={uploadInputRef}
        type="file"
        accept={acceptAttr}
        multiple
        className="hidden"
        onChange={(event) => onUploadFiles(event.target.files)}
        disabled={docsUploading}
      />
      <div className="flex items-end" style={{ gap: '8px' }}>
        <button
          onClick={_canDocs ? onToggleDocsPicker : onUpgrade}
          className="shrink-0 rounded-xl glass-icon relative flex items-center justify-center transition-all active:scale-90"
          style={{ width: '38px', height: '38px' }}
          title={_canDocs ? 'Прикрепить документ' : 'Бесплатный дневной лимит исчерпан — доступно на тарифах Лайт и Про'}
        >
          <Paperclip size={16} className={showDocsPicker ? 'text-telegram-button' : 'text-telegram-hint'} />
          {_canDocs && selectedDocCount > 0 && (
            <span
              className="absolute -top-1 -right-1 rounded-full bg-telegram-button text-white text-[9px] font-bold flex items-center justify-center"
              style={{ minWidth: '16px', height: '16px', padding: '0 4px' }}
              title={`Прикреплено документов: ${selectedDocCount}`}
            >
              {selectedDocCount}
            </span>
          )}
          {!_canDocs && (
            <span
              className="absolute -top-1 -right-1 rounded-full bg-[#ee3f58] text-white text-[7px] font-extrabold flex items-center justify-center"
              style={{ minWidth: '15px', height: '13px', padding: '0 3px' }}
            >
              PRO
            </span>
          )}
        </button>
        <WebSearchModeToggle
          mode={webSearchMode}
          onChange={onWebSearchModeChange}
          disabled={isLoading || docsUploading}
          locked={!_canWeb}
          onLockedClick={onUpgrade}
        />
        <textarea
          ref={chatInputRef}
          value={input}
          onChange={(event) => onInputChange(event.target.value)}
          onKeyDown={onKeyDown}
          maxLength={CHAT_MESSAGE_MAX_CHARS}
          placeholder="Введите вопрос..."
          rows={1}
          disabled={isLoading || docsUploading}
          className="flex-1 glass-input text-sm resize-none disabled:opacity-50"
          style={{
            padding: '10px 14px',
            minHeight: `${inputMinHeight}px`,
            maxHeight: `${inputMaxHeight}px`,
          }}
        />
        <button
          onClick={onSend}
          disabled={!input.trim() || isLoading || docsUploading}
          className="shrink-0 rounded-xl bg-telegram-button text-white flex items-center justify-center transition-all active:scale-90 disabled:opacity-40 disabled:active:scale-100"
          style={{ width: '42px', height: '42px' }}
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  )
}
